import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  Interview,
  CreateInterviewRequest,
  UpdateInterviewRequest,
  GoogleMeetLinkResponse,
  InterviewListResponse,
  MessageResponse,
  Panelist,
  RequestConfig,
} from './types';

/**
 * Interviews Module - Handles all interview scheduling API operations
 * 
 * This module provides interview management operations including scheduling,
 * Google Meet integration, and calendar invitation management.
 */
export class InterviewsModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Interview CRUD Operations
  // ============================================================================

  /**
   * Schedule a new interview
   * POST /api/admin/interviews/schedule
   */
  async scheduleInterview(
    data: CreateInterviewRequest,
    config?: RequestConfig
  ): Promise<Interview> {
    const transformedData = this.transformRequest(data);
    return this.post<Interview>('/api/admin/interviews/schedule', transformedData, config);
  }

  /**
   * Get interview details
   * GET /api/admin/interviews/:id
   */
  async getInterview(interviewId: string, config?: RequestConfig): Promise<Interview> {
    return this.get<Interview>(`/api/admin/interviews/${interviewId}`, config);
  }

  /**
   * Update an existing interview
   * PUT /api/admin/interviews/:id
   */
  async updateInterview(
    interviewId: string,
    data: UpdateInterviewRequest,
    config?: RequestConfig
  ): Promise<Interview> {
    const transformedData = this.transformRequest(data);
    return this.put<Interview>(`/api/admin/interviews/${interviewId}`, transformedData, config);
  }

  /**
   * Cancel an interview
   * DELETE /api/admin/interviews/:id
   */
  async cancelInterview(interviewId: string, config?: RequestConfig): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/interviews/${interviewId}`, config);
  }

  // ============================================================================
  // Google Meet Integration
  // ============================================================================

  /**
   * Create a Google Meet link for an interview
   * POST /api/admin/interviews/create-google-meet
   */
  async createGoogleMeetLink(
    interviewData: {
      scheduled_date: string;
      duration_minutes: number;
      attendees: string[];
      summary: string;
    },
    config?: RequestConfig
  ): Promise<GoogleMeetLinkResponse> {
    const transformedData = this.transformRequest(interviewData);
    return this.post<GoogleMeetLinkResponse>(
      '/api/admin/interviews/create-google-meet',
      transformedData,
      config
    );
  }

  // ============================================================================
  // Interview Queries
  // ============================================================================

  /**
   * Get all interviews for a candidate
   * GET /api/admin/candidates/:candidateId/interviews
   */
  async getCandidateInterviews(
    candidateId: string,
    config?: RequestConfig
  ): Promise<InterviewListResponse> {
    return this.get<InterviewListResponse>(
      `/api/admin/candidates/${candidateId}/interviews`,
      config
    );
  }

  /**
   * Get all interviews for a job
   * GET /api/admin/jobs/:jobId/interviews
   */
  async getJobInterviews(jobId: string, config?: RequestConfig): Promise<InterviewListResponse> {
    return this.get<InterviewListResponse>(`/api/admin/jobs/${jobId}/interviews`, config);
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Schedule interview with Google Meet link
   */
  async scheduleInterviewWithGoogleMeet(
    applicationId: string,
    scheduledDate: string,
    durationMinutes: number,
    interviewType: string,
    panelMembers: Array<{ email: string; name?: string; role?: string }>,
    summary: string,
    notes?: string,
    config?: RequestConfig
  ): Promise<{
    interview: Interview;
    meetLink: GoogleMeetLinkResponse;
  }> {
    try {
      // First create the Google Meet link
      const meetLink = await this.createGoogleMeetLink(
        {
          scheduled_date: scheduledDate,
          duration_minutes: durationMinutes,
          attendees: panelMembers.map(p => p.email),
          summary,
        },
        config
      );

      // Then schedule the interview with the Meet link
      const interview = await this.scheduleInterview(
        {
          application_id: applicationId,
          scheduled_date: scheduledDate,
          duration_minutes: durationMinutes,
          interview_type: interviewType,
          panel_members: panelMembers,
          notes,
          create_google_meet: true,
        },
        config
      );

      return { interview, meetLink };
    } catch (error) {
      throw this.errorHandler.handleError(error);
    }
  }

  /**
   * Reschedule an interview
   */
  async rescheduleInterview(
    interviewId: string,
    newDate: string,
    newDuration?: number,
    config?: RequestConfig
  ): Promise<Interview> {
    const updateData: UpdateInterviewRequest = {
      scheduled_date: newDate,
    };

    if (newDuration !== undefined) {
      updateData.duration_minutes = newDuration;
    }

    return this.updateInterview(interviewId, updateData, config);
  }

  /**
   * Add panel member to interview
   * Note: Panel members are managed through the update interview endpoint
   */
  async addPanelMember(
    interviewId: string,
    panelMember: { email: string; name?: string; role?: string },
    config?: RequestConfig
  ): Promise<Interview> {
    // Panel members would be added through the update endpoint
    // This is a convenience method that would need backend support
    return this.updateInterview(
      interviewId,
      { panel_members: [panelMember] },
      config
    );
  }

  /**
   * Remove panel member from interview
   * Note: Panel members are managed through the update interview endpoint
   */
  async removePanelMember(
    interviewId: string,
    _memberEmail: string,
    config?: RequestConfig
  ): Promise<Interview> {
    // Panel member removal would be handled through the update endpoint
    // This is a convenience method that would need backend support
    return this.getInterview(interviewId, config);
  }

  /**
   * Update interview notes
   * Note: Notes are managed through the update interview endpoint
   */
  async updateInterviewNotes(
    interviewId: string,
    _notes: string,
    config?: RequestConfig
  ): Promise<Interview> {
    // Notes would be updated through the update endpoint
    // This is a convenience method that would need backend support
    return this.getInterview(interviewId, config);
  }

  /**
   * Get upcoming interviews
   */
  async getUpcomingInterviews(
    jobId?: string,
    candidateId?: string,
    config?: RequestConfig
  ): Promise<InterviewListResponse> {
    let interviews: InterviewListResponse;

    if (jobId) {
      interviews = await this.getJobInterviews(jobId, config);
    } else if (candidateId) {
      interviews = await this.getCandidateInterviews(candidateId, config);
    } else {
      throw new Error('Either jobId or candidateId must be provided');
    }

    // Filter for upcoming interviews
    const now = new Date();
    return interviews.filter(interview => {
      const interviewDate = new Date(interview.scheduled_at || new Date());
      return interviewDate > now;
    });
  }

  /**
   * Get past interviews
   */
  async getPastInterviews(
    jobId?: string,
    candidateId?: string,
    config?: RequestConfig
  ): Promise<InterviewListResponse> {
    let interviews: InterviewListResponse;

    if (jobId) {
      interviews = await this.getJobInterviews(jobId, config);
    } else if (candidateId) {
      interviews = await this.getCandidateInterviews(candidateId, config);
    } else {
      throw new Error('Either jobId or candidateId must be provided');
    }

    // Filter for past interviews
    const now = new Date();
    return interviews.filter(interview => {
      const interviewDate = new Date(interview.scheduled_at || new Date());
      return interviewDate <= now;
    });
  }

  /**
   * Get interviews scheduled for today
   */
  async getTodayInterviews(
    jobId?: string,
    candidateId?: string,
    config?: RequestConfig
  ): Promise<InterviewListResponse> {
    let interviews: InterviewListResponse;

    if (jobId) {
      interviews = await this.getJobInterviews(jobId, config);
    } else if (candidateId) {
      interviews = await this.getCandidateInterviews(candidateId, config);
    } else {
      throw new Error('Either jobId or candidateId must be provided');
    }

    // Filter for today's interviews
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return interviews.filter(interview => {
      const interviewDate = new Date(interview.scheduled_at || new Date());
      return interviewDate >= today && interviewDate < tomorrow;
    });
  }

  /**
   * Get interviews for a specific date range
   */
  async getInterviewsByDateRange(
    startDate: string,
    endDate: string,
    jobId?: string,
    candidateId?: string,
    config?: RequestConfig
  ): Promise<InterviewListResponse> {
    let interviews: InterviewListResponse;

    if (jobId) {
      interviews = await this.getJobInterviews(jobId, config);
    } else if (candidateId) {
      interviews = await this.getCandidateInterviews(candidateId, config);
    } else {
      throw new Error('Either jobId or candidateId must be provided');
    }

    // Filter by date range
    const start = new Date(startDate);
    const end = new Date(endDate);

    return interviews.filter(interview => {
      if (!interview.scheduled_at) return false;
      const interviewDate = new Date(interview.scheduled_at);
      return interviewDate >= start && interviewDate <= end;
    });
  }

  /**
   * Check if interview time slot is available
   * Note: This is a placeholder implementation. In production, this would
   * query the backend to check for scheduling conflicts.
   */
  async isTimeSlotAvailable(
    _scheduledDate: string,
    _durationMinutes: number,
    _panelMemberEmails: string[],
    _config?: RequestConfig
  ): Promise<boolean> {
    // This would need to check against all interviews for the panel members
    // For now, we'll return true as a placeholder
    // In a real implementation, this would query the backend for conflicts
    return true;
  }

  /**
   * Format interview for calendar
   */
  formatForCalendar(interview: Interview): {
    title: string;
    start: Date;
    end: Date;
    description: string;
    location: string;
  } {
    if (!interview.scheduled_at) {
      return { 
        title: '', 
        start: new Date(), 
        end: new Date(), 
        description: '', 
        location: '' 
      };
    }
    const start = new Date(interview.scheduled_at);
    const end = new Date(start.getTime() + interview.duration_minutes * 60000);

    return {
      title: `Interview - ${interview.status}`,
      start,
      end,
      description: `Interview for candidate ${interview.candidate_id}`,
      location: interview.meet_link || 'TBD',
    };
  }

  /**
   * Validate interview data
   */
  validateInterviewData(data: CreateInterviewRequest | UpdateInterviewRequest): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate scheduled date
    if ('scheduled_date' in data && data.scheduled_date) {
      const scheduledDate = new Date(data.scheduled_date);
      const now = new Date();
      
      if (isNaN(scheduledDate.getTime())) {
        errors.push('Invalid scheduled date format');
      } else if (scheduledDate < now) {
        errors.push('Scheduled date cannot be in the past');
      }
    }

    // Validate duration
    if ('duration_minutes' in data && data.duration_minutes !== undefined) {
      if (data.duration_minutes <= 0) {
        errors.push('Duration must be greater than 0');
      }
      if (data.duration_minutes > 480) {
        errors.push('Duration cannot exceed 8 hours (480 minutes)');
      }
    }

    // Validate panel members
    if ('panel_members' in data && data.panel_members) {
      if (data.panel_members.length === 0) {
        errors.push('At least one panel member is required');
      }
      
      data.panel_members.forEach((member, index) => {
        if (!member.email || !this.isValidEmail(member.email)) {
          errors.push(`Invalid email for panel member ${index + 1}`);
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Calculate interview end time
   */
  calculateEndTime(scheduledDate: string, durationMinutes: number): Date {
    const start = new Date(scheduledDate);
    return new Date(start.getTime() + durationMinutes * 60000);
  }

  /**
   * Check if interview is in progress
   */
  isInterviewInProgress(interview: Interview): boolean {
    if (!interview.scheduled_at) return false;
    const now = new Date();
    const start = new Date(interview.scheduled_at);
    const end = this.calculateEndTime(interview.scheduled_at, interview.duration_minutes);
    
    return now >= start && now <= end;
  }

  /**
   * Check if interview is upcoming (within next 24 hours)
   */
  isInterviewUpcoming(interview: Interview): boolean {
    if (!interview.scheduled_at) return false;
    const now = new Date();
    const start = new Date(interview.scheduled_at);
    const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    return start > now && start <= twentyFourHoursFromNow;
  }

  /**
   * Get all panelists for dropdown suggestions
   * GET /api/admin/panelists
   */
  async getPanelists(config?: RequestConfig): Promise<Panelist[]> {
    return this.get<Panelist[]>('/api/admin/panelists', config);
  }
}
